<template>
  <v-text-field v-bind="attributes"
                v-on="listeners"
                :value="value"
                :error="hasFieldErrors"
                @change="change"
                ref="proxy">
    <slot v-for="slot in Object.keys($slots)" :name="slot" :slot="slot"/>
    <template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
      <slot :name="slot" v-bind="scope"/>
    </template>
    <template #append>
      <v-dialog persistent content-class="c-dialog" v-model="showPicker" max-width="290">
        <template v-slot:activator="{ on, attrs }">
          <div class="v-input__icon"> <!-- this class needed to correct appearance when outlined style is used on the input -->
            <v-btn icon color="primary" :aria-label="$t('cove.datepicker.openPicker')" v-bind="attrs" v-on="on" ref="activator"
                   :ripple="false">
              <v-icon>mdi-calendar-month</v-icon>
            </v-btn>
          </div>
        </template>
        <v-toolbar :color="color" dark dense>
          <v-toolbar-title>
            {{ $t('cove.datepicker.selectDate') }}
          </v-toolbar-title>
        </v-toolbar>
        <v-date-picker no-title :value="isoDate" @input="dateSelected" :color="color">
          <v-spacer/>
          <v-btn text class="mr-1 mb-1 px-3" :color="color" @click="showPicker = false">
            {{ $t('cove.general.cancel') }}
          </v-btn>
        </v-date-picker>
      </v-dialog>
    </template>
  </v-text-field>
</template>

<script>
import { DateTime } from 'luxon';
import InputMixin from '../mixins/input';

const DEFAULTS = {
  clearable: false,
  autocomplete: 'off',
  spellcheck: 'false',
  autocorrect: 'off',
  autocapitalize: 'off',
  maxlength: '10'
};

export default {
  mixins: [ InputMixin ],
  props: {
    color: {
      type: String,
      default: 'primary'
    }
  },
  data () {
    return {
      showPicker: false,
      // define the listener here in data so it's unique per dialog instance
      escListener: (e) => {
        if (e.keyCode === 27)
        {
          this.showPicker = false;
        }
      }
    };
  },
  // mounted hook so we can attempt to parse any pre-loaded date shortcut values, e.g. c+5
  mounted () {
    this.change(this.value);
  },
  computed: {
    isoDate () {
      let result = null;
      let parsed = this.$cove.parseDate(this.value);
      if (parsed !== null)
      {
        result = parsed.toISODate();
      }

      return result;
    },
    attributes () {
      let result = Object.assign({}, DEFAULTS, this.$attrs);
      this.mixinRequired(result);

      return result;
    },
    listeners () {
      return this.$listeners;
    }
  },
  watch: {
    showPicker (newValue) {
      if (newValue)
      {
        window.addEventListener('keydown', this.escListener);
        setTimeout(() => {
          document.querySelector('.v-dialog__content--active .v-picker button').focus();
        }, 125);
      }
      else
      {
        window.removeEventListener('keydown', this.escListener);
        setTimeout(() => {
          this.$refs.activator.$el.focus();
        }, 125);
      }
    }
  },
  methods: {
    change (value) {
      let promise = null;
      let result = this.trimToNull(value);

      if (result)
      {
        let date = this.$cove.parseDate(result);
        if (date !== null)
        {
          promise = Promise.resolve(this.$cove.formatDate(date));
        }
        else
        {
          promise = this.$cove.processDatePickerShortcut(result);
        }
      }
      else
      {
        promise = Promise.resolve(result);
      }

      promise.then((v) => {
        if (v !== value)
        {
          this.$emit('input', v);
          this.$emit('change', v);
        }
      });
    },
    dateSelected (str) {
      let isoDate = DateTime.fromISO(str);
      let result = this.$cove.formatDate(isoDate);
      this.$emit('input', result);
      this.$emit('change', result);
      this.showPicker = false;
    }
  }
};
</script>
